import { ComponentType } from 'react';
import { Capacitor } from '@capacitor/core';
import type {} from '@mui/lab/themeAugmentation';
import { createTheme, Theme, alpha, lighten } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { grey } from '@mui/material/colors';
import * as Device from 'react-device-detect';

import RemoveIcon from '@mui/icons-material/Remove';
import TranslateIcon from '@mui/icons-material/Translate';
import LanguageIcon from '@mui/icons-material/Language';
import WebIcon from '@mui/icons-material/Web';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ListIcon from '@mui/icons-material/List';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import AirlineSeatIndividualSuiteIcon from '@mui/icons-material/AirlineSeatIndividualSuite';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MenuIcon from '@mui/icons-material/Menu';
import ExploreIcon from '@mui/icons-material/Explore';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import SummarizeIcon from '@mui/icons-material/Summarize';
import StarsIcon from '@mui/icons-material/Stars';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import UpcomingIcon from '@mui/icons-material/Upcoming';
import QuizIcon from '@mui/icons-material/Quiz';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupsIcon from '@mui/icons-material/Groups';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TelegramIcon from '@mui/icons-material/Telegram';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import GridOnIcon from '@mui/icons-material/GridOn';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import TimelineIcon from '@mui/icons-material/Timeline';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import DeleteIcon from '@mui/icons-material/Delete';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonAdd from '@mui/icons-material/PersonAdd';
import ShieldIcon from '@mui/icons-material/Shield';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EditNotificationsIcon from '@mui/icons-material/EditNotifications';
import EventIcon from '@mui/icons-material/Event';
import DateRangeIcon from '@mui/icons-material/DateRange';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TuneIcon from '@mui/icons-material/Tune';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import CreateIcon from '@mui/icons-material/Create';
import ReviewsIcon from '@mui/icons-material/Reviews';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import StoreIcon from '@mui/icons-material/Store';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import FlareIcon from '@mui/icons-material/Flare';
import SendIcon from '@mui/icons-material/Send';
import SpaIcon from '@mui/icons-material/Spa';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import CloseIcon from '@mui/icons-material/Close';
import PreviewIcon from '@mui/icons-material/Preview';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ModeIcon from '@mui/icons-material/Mode';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import UpdateIcon from '@mui/icons-material/Update';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import RestoreIcon from '@mui/icons-material/Restore';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HomeIcon from '@mui/icons-material/Home';
import GitHubIcon from '@mui/icons-material/GitHub';
import SearchIcon from '@mui/icons-material/Search';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import ShareIcon from '@mui/icons-material/Share';
import Logout from '@mui/icons-material/Logout';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeMuteIcon from '@mui/icons-material/VolumeMute';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

import { SvgIconProps } from '@mui/material/SvgIcon';

import { Transition } from '@components';

declare module '@mui/material/styles' {
  interface CustomTheme {
    borderRadius: number;
    boxShadow: string;
    elevation: number;
    icons: {
      logo: ComponentType<SvgIconProps>;
      webpage: ComponentType<SvgIconProps>;
      account: ComponentType<SvgIconProps>;
      booking: ComponentType<SvgIconProps>;
      store: ComponentType<SvgIconProps>;
      calendar: ComponentType<SvgIconProps>;
      inbox: ComponentType<SvgIconProps>;
      lock: ComponentType<SvgIconProps>;
      dashboard: ComponentType<SvgIconProps>;
      // actions
      translate: ComponentType<SvgIconProps>;
      language: ComponentType<SvgIconProps>;
      burgerMenu: ComponentType<SvgIconProps>;
      explore: ComponentType<SvgIconProps>;
      close: ComponentType<SvgIconProps>;
      copy: ComponentType<SvgIconProps>;
      whatsapp: ComponentType<SvgIconProps>;
      sms: ComponentType<SvgIconProps>;
      back: ComponentType<SvgIconProps>;
      manageAccount: ComponentType<SvgIconProps>;
      delete: ComponentType<SvgIconProps>;
      favorite: ComponentType<SvgIconProps>;
      favoriteOutlined: ComponentType<SvgIconProps>;
      deleteOutlined: ComponentType<SvgIconProps>;
      deleteForever: ComponentType<SvgIconProps>;
      add: ComponentType<SvgIconProps>;
      send: ComponentType<SvgIconProps>;
      options: ComponentType<SvgIconProps>;
      publish: ComponentType<SvgIconProps>;
      postpone: ComponentType<SvgIconProps>;
      draft: ComponentType<SvgIconProps>;
      addPerson: ComponentType<SvgIconProps>;
      change: ComponentType<SvgIconProps>;
      restore: ComponentType<SvgIconProps>;
      logout: ComponentType<SvgIconProps>;
      restoreTrash: ComponentType<SvgIconProps>;
      edit: ComponentType<SvgIconProps>;
      see: ComponentType<SvgIconProps>;
      hide: ComponentType<SvgIconProps>;
      openNew: ComponentType<SvgIconProps>;
      signup: ComponentType<SvgIconProps>;
      share: ComponentType<SvgIconProps>;
      search: ComponentType<SvgIconProps>;
      advancedFilter: ComponentType<SvgIconProps>;
      filter: ComponentType<SvgIconProps>;
      filterClear: ComponentType<SvgIconProps>;
      news: ComponentType<SvgIconProps>;
      github: ComponentType<SvgIconProps>;
      upload: ComponentType<SvgIconProps>;
      interested: ComponentType<SvgIconProps>;
      home: ComponentType<SvgIconProps>;
      move: ComponentType<SvgIconProps>;
      rocket: ComponentType<SvgIconProps>;
      summary: ComponentType<SvgIconProps>;
      restorePage: ComponentType<SvgIconProps>;
      myLocation: ComponentType<SvgIconProps>;
      write: ComponentType<SvgIconProps>;
      // notifications
      request: ComponentType<SvgIconProps>;
      feedback: ComponentType<SvgIconProps>;
      telegram: ComponentType<SvgIconProps>;
      // others
      friends: ComponentType<SvgIconProps>;
      update: ComponentType<SvgIconProps>;
      info: ComponentType<SvgIconProps>;
      infoOutlined: ComponentType<SvgIconProps>;
      artist: ComponentType<SvgIconProps>;
      noImage: ComponentType<SvgIconProps>;
      email: ComponentType<SvgIconProps>;
      event: ComponentType<SvgIconProps>;
      week: ComponentType<SvgIconProps>;
      price: ComponentType<SvgIconProps>;
      faq: ComponentType<SvgIconProps>;
      grid: ComponentType<SvgIconProps>;
      location: ComponentType<SvgIconProps>;
      people: ComponentType<SvgIconProps>;
      spaces: ComponentType<SvgIconProps>;
      check: ComponentType<SvgIconProps>;
      unfold: ComponentType<SvgIconProps>;
      image: ComponentType<SvgIconProps>;
      flare: ComponentType<SvgIconProps>;
      vege: ComponentType<SvgIconProps>;
      expandMore: ComponentType<SvgIconProps>;
      expandLess: ComponentType<SvgIconProps>;
      chevron: ComponentType<SvgIconProps>;
      settings: ComponentType<SvgIconProps>;
      shield: ComponentType<SvgIconProps>;
      userGear: ComponentType<SvgIconProps>;
      ticket: ComponentType<SvgIconProps>;
      notification: ComponentType<SvgIconProps>;
      notificationEdit: ComponentType<SvgIconProps>;
      list: ComponentType<SvgIconProps>;
      preview: ComponentType<SvgIconProps>;
      timeline: ComponentType<SvgIconProps>;
      timelapse: ComponentType<SvgIconProps>;
      sleep: ComponentType<SvgIconProps>;
      restaurant: ComponentType<SvgIconProps>;
      volMin: ComponentType<SvgIconProps>;
      volMid: ComponentType<SvgIconProps>;
      volMax: ComponentType<SvgIconProps>;
      music: ComponentType<SvgIconProps>;
      report: ComponentType<SvgIconProps>;
      accounts: ComponentType<SvgIconProps>;
      minus: ComponentType<SvgIconProps>;
    };
  }

  interface Theme extends CustomTheme {}
  interface ThemeOptions extends CustomTheme {}
}

const icons = {
  logo: BlurOnIcon,
  // pages
  webpage: WebIcon,
  booking: RoomServiceIcon,
  store: StoreIcon,
  account: AccountCircleIcon,
  calendar: CalendarMonthIcon,
  inbox: InboxIcon,
  lock: LockIcon,
  settings: SettingsIcon,
  shield: ShieldIcon,
  userGear: SettingsIcon,
  home: HomeIcon,
  github: GitHubIcon,
  news: NewspaperIcon,
  dashboard: DashboardIcon,
  // Actions
  translate: TranslateIcon,
  language: LanguageIcon,
  burgerMenu: MenuIcon,
  explore: ExploreIcon,
  search: SearchIcon,
  advancedFilter: TuneIcon,
  filter: FilterAltIcon,
  filterClear: FilterAltOffIcon,
  change: ChangeCircleIcon,
  back: ChevronLeftIcon,
  copy: ContentCopyIcon,
  whatsapp: WhatsAppIcon,
  sms: SmsIcon,
  spaces: SpaceDashboardIcon,
  add: AddIcon,
  openNew: OpenInNewIcon,
  close: CloseIcon,
  chevron: ChevronRightIcon,
  delete: DeleteIcon,
  addPerson: PersonAdd,
  share: ShareIcon,
  deleteOutlined: DeleteOutlineIcon,
  deleteForever: DeleteForeverIcon,
  favorite: FavoriteIcon,
  favoriteOutlined: FavoriteBorderIcon,
  manageAccount: ManageAccountsIcon,
  expandLess: ExpandLess,
  logout: Logout,
  unfold: UnfoldMoreIcon,
  expandMore: ExpandMore,
  options: MoreVertIcon,
  send: SendIcon,
  interested: LightbulbIcon,
  publish: TaskAltIcon,
  postpone: UpdateIcon,
  move: OpenWithIcon,
  draft: UnpublishedIcon,
  restore: RestoreIcon,
  restoreTrash: RestoreFromTrashIcon,
  myLocation: MyLocationIcon,
  edit: ModeIcon,
  see: Visibility,
  hide: VisibilityOff,
  signup: AppRegistrationIcon,
  rocket: RocketLaunchIcon,
  summary: SummarizeIcon,
  restorePage: RestorePageIcon,
  upload: CloudUploadIcon,
  write: CreateIcon,
  // notifications
  request: UpcomingIcon,
  feedback: ReviewsIcon,
  telegram: TelegramIcon,
  // others
  friends: Diversity3Icon,
  artist: StarsIcon,
  info: InfoIcon,
  infoOutlined: InfoOutlinedIcon,
  noImage: ImageNotSupportedIcon,
  people: GroupsIcon,
  update: GroupsIcon,
  event: EventIcon,
  week: DateRangeIcon,
  email: AlternateEmailIcon,
  price: AttachMoneyIcon,
  location: LocationOnIcon,
  check: CheckIcon,
  flare: FlareIcon,
  grid: GridOnIcon,
  ticket: LocalActivityIcon,
  faq: QuizIcon,
  vege: SpaIcon,
  notification: NotificationsActiveIcon,
  notificationEdit: EditNotificationsIcon,
  image: ImageIcon,
  list: ListIcon,
  preview: PreviewIcon,
  timeline: TimelineIcon,
  timelapse: TimelapseIcon,
  sleep: AirlineSeatIndividualSuiteIcon,
  restaurant: RestaurantIcon,
  volMin: VolumeMuteIcon,
  volMid: VolumeDownIcon,
  volMax: VolumeUpIcon,
  music: MusicNoteIcon,
  report: CrisisAlertIcon,
  accounts: AccountTreeIcon,
  minus: RemoveIcon,
};

const { palette } = createTheme();
const { augmentColor } = palette;
const createColor = (mainColor: string) => augmentColor({ color: { main: mainColor } });

// define light theme colors
export const lightTheme: Theme = createTheme({
  typography: {
    fontFamily: '"Exo", "Roboto", "Helvetica", "Arial", "Style Script", sans-serif',
  },
  // components: {
  //   MuiDataGrid: {
  //     styleOverrides: {
  //       root: {
  // backgroundColor: 'red',
  //       },
  //     },
  //   },
  // },
  palette: {
    primary: {
      main: '#6100ff',
      light: '#a066ff',
      dark: '#3a0099',
    },
    secondary: {
      main: '#E7901C',
      light: '#ffa93a',
      dark: 'cc7606',
    },
    background: {
      default: '#EFF2F5',
    },
    uncolored: createColor('#939393'),
  },
  borderRadius: 3,
  boxShadow: `0px 5px 10px ${alpha(grey[300], 1)}`,
  elevation: 10,
  icons,
});

// define dark theme colors
export const darkTheme: Theme = createTheme({
  typography: {
    fontFamily: '"Exo", "Roboto", "Helvetica", "Arial", "Style Script", sans-serif',
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          backdropFilter: 'blur(60px)',
          borderBottom: `1px solid ${alpha('#ffffff', 0.1)} `,
          paddingLeft: theme.spacing(Device.isMobile ? 0 : 2),
          paddingRight: theme.spacing(Device.isMobile ? 0 : 2),
          paddingTop: 'env(safe-area-inset-top)',
        }),
      },
    },
    MuiDialog: {
      defaultProps: {
        TransitionComponent: Transition,
      },
      styleOverrides: {
        root: {
          // hide scrollbars globally inside dialog
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          '& *': {
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          },
          // backdrop
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(5px)',
          },
        },
        paper: ({ theme }) => ({
          minHeight: Device.isDesktop ? '90dvh' : undefined,
          border: Device.isMobile ? undefined : '1px solid rgba(255, 255, 255, 0.1)',
          paddingLeft: theme.spacing(0), //
          paddingRight: theme.spacing(0), //
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    // MuiBackdrop: {
    //   styleOverrides: {
    //     root: {
    //       backgroundColor: 'rgba(0, 0, 0, 0.5)',
    //       backdropFilter: 'blur(5px)',
    //     },
    //   },
    // },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.grey[400],
          '&::before, &::after': {
            // for horizontal divider with children
            borderColor: theme.palette.grey[400],
          },
        }),
        vertical: ({ theme }) => ({
          borderColor: theme.palette.grey[800],
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {},
        outlined: {
          '&.MuiButton-outlinedUncolored': {
            color: '#999999',
            borderColor: '#999999',
            '&:hover': {
              borderColor: '#CCCCCC',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          },
        },
        outlinedPrimary: ({ theme }) => ({
          backgroundColor: 'black',
          color: lighten(theme.palette.primary.main, 0.2),
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& label': {
            color: '#FFFFFF',
          },
          '& label.Mui-focused': {
            color: '#FFFFFF',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#CCCCCC',
          '&.Mui-focused': {
            color: '#FFFFFF',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: '#999999 !important',
          },
          '&:hover fieldset': {
            borderColor: '#CCCCCC !important',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#CCCCCC !important',
            // borderColor: '#6100ff !important',
          },
          '&:not(.Mui-focused):not(.Mui-error):not(:hover) fieldset': {
            borderColor: '#999999',
          },
          '&.Mui-disabled fieldset': {
            borderColor: '#4c4c4c !important', // Ici, la bordure devient rouge
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: '#999999',
          },
          '&:hover fieldset': {
            borderColor: '#CCCCCC',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#6100ff',
          },
        },
        underline: {
          '&:before': {
            borderBottom: '1px solid #999999',
          },
          '&:hover:before': {
            borderBottom: '1px solid #CCCCCC',
          },
          '&:after': {
            borderBottom: '2px solid #6100ff',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottom: '1px solid #999999',
          },
          '&:hover:before': {
            borderBottom: '1px solid #CCCCCC',
          },
          '&:after': {
            borderBottom: '2px solid #6100ff',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: '#FFFFFF', // Chevron blanc par défaut
          '&:hover': {
            color: '#CCCCCC', // Chevron en hover
          },
          '&.Mui-focused': {
            color: '#6100ff', // Chevron quand focus
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#4c4c4c',
          },
          '&.Mui-selected': {
            backgroundColor: '#3a0099',
            '&:hover': {
              backgroundColor: '#3a0099',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        sizeSmall: {
          color: '#CCCCCC',
          '&:hover': {
            color: '#ffffff',
          },
        },
        sizeLarge: {
          color: '#CCCCCC',
          '&:hover': {
            color: '#ffffff',
          },
        },
        sizeMedium: {
          color: '#CCCCCC',
          '&:hover': {
            color: '#ffffff',
          },
        },
      },
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: {
          color: '#CCCCCC',
          '&:hover': {
            color: '#ffffff',
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        switchViewButton: {
          color: '#CCCCCC',
          '&:hover': {
            color: '#ffffff',
          },
        },
      },
    },
  },
  palette: {
    primary: {
      main: '#6100ff',
      light: '#8700ff',
      dark: '#3a0099',
    },
    secondary: {
      main: '#E7901C',
      light: '#ffa93a',
      dark: 'cc7606',
    },
    background: {
      default: '#141414',
      paper: '#262626',
      // paper: '#1e1e1e',
    },
    text: {
      primary: '#F2F2F2',
      secondary: '#A5A5A5',
      disabled: '#666666',
    },
    action: {
      disabled: '#666666',
      disabledBackground: 'black',
    },
    uncolored: createColor('#4c4c4c'),
  },
  borderRadius: 3,
  boxShadow: `0px 5px 10px ${alpha('#000', 0.3)}`,
  elevation: 10,
  icons,
});
